'use client';

import type {
  CategoriesEntity,
  GeneralLayout,
} from '@eliadmin/core/src/api/general/types/general-layout-types';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { LuMenu, LuX } from 'react-icons/lu';
import { Logo } from '@eliadmin/core/src/components/ui/logo';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import Image from 'next/image';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';

export type MobileSidebarProps = {
  generalLayout: GeneralLayout['data'];
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
  scrolled: boolean;
};

const MobileSidebar = (props: MobileSidebarProps) => {
  return (
    <>
      <Sheet>
        <SheetTrigger
          aria-label="Otvoriť menu"
          className={cn('ea-mobile-sidebar__trigger', props.className?.trigger)}
        >
          <LuMenu
            size={24}
            className={cn('transition-all', props?.scrolled ? 'text-black' : 'text-white')}
          />
        </SheetTrigger>
        <SheetContent
          accessibleTitle="Navigácia"
          side={props.side}
          className={cn(
            'hide-scroll-bar max-w-[360px] overflow-y-scroll rounded-r-[16px] border-none bg-white p-0',
            'ea-mobile-sidebar__content',
            props.className?.content,
          )}
          closeIcon={{
            wrapper: 'right-4 top-4 hidden',
            icon: 'text-primary h-6 w-6 font-bold',
          }}
        >
          <div className="flex w-full items-center justify-between px-5 py-4">
            <Logo
              className={{ image: 'h-6' }}
              generalLayout={props.generalLayout}
              src={props.generalLayout?.footer?.logo?.url}
            />
            <SheetClose aria-label="Zavrieť menu">
              <LuX size={24} className="text-[#292D32]" />
            </SheetClose>
          </div>
          <div className="flex h-full w-full flex-col items-center justify-center gap-16 px-5">
            <div className="flex w-full flex-col items-center justify-center gap-8">
              <div className="flex flex-col items-center justify-center gap-4">
                {props.menu?.map(field => (
                  <SheetClose
                    asChild
                    key={field.name}
                    className="h5 text-center font-medium text-black"
                  >
                    <Link prefetchOnHover href={getSafeHref(field?.path ?? '#')}>
                      {field.name}
                    </Link>
                  </SheetClose>
                ))}
                <SheetClose asChild className="h5 text-center font-medium text-black">
                  <Link prefetchOnHover href={getSafeHref('#kontakt')}>
                    Kontakt
                  </Link>
                </SheetClose>
              </div>
              <SheetClose asChild className="w-full">
                <Button asChild className="w-full">
                  <Link prefetchOnHover href={'#kontakt'}>
                    Konzultácia zdarma
                  </Link>
                </Button>
              </SheetClose>
            </div>
            <div className="flex w-full flex-col items-center justify-center gap-4">
              <Heading title="Potrebujete poradiť?" center tag="h5" as="h3" />
              <div className="flex w-full flex-col items-start justify-start gap-3 sm:gap-4">
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center justify-start gap-3 lg:gap-4">
                    <div className="flex max-h-[42px] min-h-[42px] min-w-[42px] max-w-[42px] items-center justify-center rounded-[8px] bg-black/[0.08] lg:max-h-[54px] lg:min-h-[54px] lg:min-w-[54px] lg:max-w-[54px]">
                      <Image
                        src={'/images/icons/contact/phone-primary.svg'}
                        alt="Zavolať"
                        width={20}
                        height={20}
                      />
                    </div>
                    <div className="flex flex-col items-start justify-start gap-[1px]">
                      <Paragraph size="base" color="black">
                        Zavolajte nám
                      </Paragraph>
                      <Paragraph size="lg" color="black" className="font-medium">
                        {props.generalLayout?.contact?.phone}
                      </Paragraph>
                    </div>
                  </div>
                  <Link
                    prefetchOnHover
                    href={`tel:${props.generalLayout?.contact?.phone}`}
                    className="text-sm font-medium text-[#4E4BBE] transition-all hover:opacity-80 lg:text-base"
                    target="_blank"
                  >
                    Zavolať
                  </Link>
                </div>
                <div className="h-[1px] w-full bg-black/10" />
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center justify-start gap-3 lg:gap-4">
                    <div className="flex max-h-[42px] min-h-[42px] min-w-[42px] max-w-[42px] items-center justify-center rounded-[8px] bg-black/[0.08] lg:max-h-[54px] lg:min-h-[54px] lg:min-w-[54px] lg:max-w-[54px]">
                      <Image
                        src={'/images/icons/contact/email-primary.svg'}
                        alt="Napísať e-mail"
                        width={20}
                        height={20}
                      />
                    </div>
                    <div className="flex flex-col items-start justify-start gap-[1px]">
                      <Paragraph size="base" color="black">
                        Napíšte nám
                      </Paragraph>
                      <Paragraph size="lg" color="black" className="font-medium">
                        {props.generalLayout?.contact?.email}
                      </Paragraph>
                    </div>
                  </div>
                  <Link
                    prefetchOnHover
                    href={`mailto:${props.generalLayout?.contact?.email}`}
                    className="text-sm font-medium text-[#4E4BBE] transition-all hover:opacity-80 lg:text-base"
                    target="_blank"
                  >
                    Napísať e-mail
                  </Link>
                </div>
                <div className="h-[1px] w-full bg-black/10" />
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center justify-start gap-3 lg:gap-4">
                    <div className="flex max-h-[42px] min-h-[42px] min-w-[42px] max-w-[42px] items-center justify-center rounded-[8px] bg-black/[0.08] lg:max-h-[54px] lg:min-h-[54px] lg:min-w-[54px] lg:max-w-[54px]">
                      <Image
                        src={'/images/icons/contact/location-primary.svg'}
                        alt="Pôsobenie"
                        width={20}
                        height={20}
                      />
                    </div>
                    <div className="flex flex-col items-start justify-start gap-[1px]">
                      <Paragraph size="base" color="black">
                        Pôsobenie
                      </Paragraph>
                      <Paragraph size="lg" color="black" className="font-medium">
                        Podľa potreby prevádzky
                      </Paragraph>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
